import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import FilterPanel from './FilterPanel';
import EditModal from './EditModal';
import AddRowModal from './AddRowModal';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiEdit2, FiTrash2, FiPlus, FiChevronLeft, FiChevronRight, FiLoader, FiFilter, FiAlertCircle } = FiIcons;

const DataTable = ({ database, table, uuid, apiBaseUrl = '/api' }) => {
  const [data, setData] = useState([]);
  const [structure, setStructure] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 0 });
  const [filters, setFilters] = useState({ search: '', column: '', sortBy: '', sortOrder: 'ASC' });
  const [editingRow, setEditingRow] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [cachedData, setCachedData] = useState({});
  const [cachedStructures, setCachedStructures] = useState({});
  
  // Optimized fetch function with memoization and batching
  const fetchTableStructure = useCallback(async () => {
    // Check if we already have the structure cached
    const cacheKey = `structure_${database}_${table}`;
    if (cachedStructures[cacheKey]) {
      console.log("Using cached structure data");
      setStructure(cachedStructures[cacheKey]);
      return;
    }
    
    try {
      setLoading(true);
      const response = await fetch(
        `${apiBaseUrl}/structure/${encodeURIComponent(database)}/${encodeURIComponent(table)}?uuid=${encodeURIComponent(uuid)}`,
        {
          headers: {
            'Cache-Control': 'max-age=3600', // Allow caching for structure data
          }
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || errorData.details || 'Failed to fetch table structure');
      }
      
      const data = await response.json();
      setStructure(data);
      
      // Cache the structure data
      setCachedStructures(prev => ({ ...prev, [cacheKey]: data }));
    } catch (err) {
      console.error('Structure fetch error:', err);
      setError(err.message || 'Failed to fetch table structure');
    } finally {
      setLoading(false);
    }
  }, [database, table, uuid, apiBaseUrl, cachedStructures]);

  // Optimized data fetching with cache
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Create unique cache key for this specific data request
      const filterKey = JSON.stringify(filters);
      const cacheKey = `data_${database}_${table}_page${pagination.page}_${filterKey}`;
      
      // Check if we have this data cached and it's not stale (< 30 seconds old)
      if (cachedData[cacheKey] && cachedData[cacheKey].timestamp > Date.now() - 30000) {
        console.log("Using cached data");
        setData(cachedData[cacheKey].data);
        setPagination(prev => ({ ...prev, ...cachedData[cacheKey].pagination }));
        setLoading(false);
        return;
      }
      
      // Build query parameters
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
        uuid: uuid,
        ...filters
      });
      
      // Fetch data with performance optimizations
      const response = await fetch(
        `${apiBaseUrl}/data/${encodeURIComponent(database)}/${encodeURIComponent(table)}?${params}`,
        {
          headers: {
            'Cache-Control': 'no-cache', // Don't cache data responses on the server
          }
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || errorData.details || 'Failed to fetch data');
      }
      
      const result = await response.json();
      
      // Update state in batch to prevent multiple renders
      setData(result.data);
      setPagination(prev => ({ ...prev, ...result.pagination }));
      setError('');
      
      // Cache the data with timestamp
      setCachedData(prev => ({
        ...prev,
        [cacheKey]: {
          data: result.data,
          pagination: result.pagination,
          timestamp: Date.now()
        }
      }));
    } catch (err) {
      console.error('Data fetch error:', err);
      setError(err.message || 'Failed to fetch table data');
    } finally {
      setLoading(false);
    }
  }, [database, table, uuid, apiBaseUrl, pagination.page, pagination.limit, filters, cachedData]);

  // Combined fetching of structure and data for better performance
  useEffect(() => {
    if (database && table && uuid) {
      console.log("DataTable: Loading data for", database, table);
      
      // Use Promise.all to fetch structure and data in parallel
      const fetchAll = async () => {
        await Promise.all([
          fetchTableStructure(),
          fetchData()
        ]);
      };
      
      fetchAll();
    } else {
      console.log("DataTable: Missing required parameters", { database, table, uuid: uuid ? "present" : "missing" });
    }
  }, [database, table, uuid, fetchTableStructure, fetchData]);

  const handleEdit = (row) => {
    setEditingRow(row);
  };

  const handleSave = async (updatedData) => {
    try {
      const primaryKey = structure.find(col => col.Key === 'PRI')?.Field || structure[0]?.Field;
      const id = editingRow[primaryKey];
      
      const response = await fetch(
        `${apiBaseUrl}/data/${encodeURIComponent(database)}/${encodeURIComponent(table)}/${encodeURIComponent(id)}?uuid=${encodeURIComponent(uuid)}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ data: updatedData, primaryKey })
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || errorData.details || 'Failed to update row');
      }
      
      // Invalidate all cached data for this table
      const newCachedData = { ...cachedData };
      Object.keys(newCachedData).forEach(key => {
        if (key.startsWith(`data_${database}_${table}`)) {
          delete newCachedData[key];
        }
      });
      setCachedData(newCachedData);
      
      // Refresh data
      await fetchData();
      setEditingRow(null);
    } catch (err) {
      alert('Failed to update row: ' + (err.message || 'Unknown error'));
    }
  };

  const handleDelete = async (row) => {
    if (!confirm('Are you sure you want to delete this row?')) return;
    
    try {
      const primaryKey = structure.find(col => col.Key === 'PRI')?.Field || structure[0]?.Field;
      const id = row[primaryKey];
      
      const response = await fetch(
        `${apiBaseUrl}/data/${encodeURIComponent(database)}/${encodeURIComponent(table)}/${encodeURIComponent(id)}?primaryKey=${encodeURIComponent(primaryKey)}&uuid=${encodeURIComponent(uuid)}`,
        {
          method: 'DELETE'
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || errorData.details || 'Failed to delete row');
      }
      
      // Invalidate cache for this table
      const newCachedData = { ...cachedData };
      Object.keys(newCachedData).forEach(key => {
        if (key.startsWith(`data_${database}_${table}`)) {
          delete newCachedData[key];
        }
      });
      setCachedData(newCachedData);
      
      await fetchData();
    } catch (err) {
      alert('Failed to delete row: ' + (err.message || 'Unknown error'));
    }
  };

  const handleAdd = async (newData) => {
    try {
      const response = await fetch(
        `${apiBaseUrl}/data/${encodeURIComponent(database)}/${encodeURIComponent(table)}?uuid=${encodeURIComponent(uuid)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ data: newData })
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || errorData.details || 'Failed to add row');
      }
      
      // Invalidate cache for this table
      const newCachedData = { ...cachedData };
      Object.keys(newCachedData).forEach(key => {
        if (key.startsWith(`data_${database}_${table}`)) {
          delete newCachedData[key];
        }
      });
      setCachedData(newCachedData);
      
      await fetchData();
      setShowAddModal(false);
    } catch (err) {
      alert('Failed to add row: ' + (err.message || 'Unknown error'));
    }
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleRetry = () => {
    setError('');
    fetchTableStructure();
    fetchData();
  };

  // Memoize the filtered data to prevent unnecessary re-renders
  const memoizedData = useMemo(() => {
    return data;
  }, [data]);

  // Memoize the structure data
  const memoizedStructure = useMemo(() => {
    return structure;
  }, [structure]);

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-8 text-center">
        <div className="text-red-500 text-4xl mb-4">
          <SafeIcon icon={FiAlertCircle} />
        </div>
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Error</h3>
        <p className="text-gray-600 mb-4">{error}</p>
        <button
          onClick={handleRetry}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg">
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-800">
            {table} Data
          </h2>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`px-4 py-2 rounded-lg transition-colors flex items-center ${
                showFilters ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <SafeIcon icon={FiFilter} className="mr-2" />
              Filters
            </button>
            <button
              onClick={() => setShowAddModal(true)}
              className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center"
            >
              <SafeIcon icon={FiPlus} className="mr-2" />
              Add Row
            </button>
          </div>
        </div>
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <FilterPanel
                columns={memoizedStructure.map(col => col.Field)}
                filters={filters}
                onFilterChange={handleFilterChange}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <SafeIcon icon={FiLoader} className="animate-spin text-blue-500 mr-2 text-2xl" />
            <span className="text-gray-600">Loading data...</span>
          </div>
        ) : memoizedData.length > 0 ? (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                {memoizedStructure.map((column) => (
                  <th
                    key={column.Field}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200"
                  >
                    <div className="flex items-center">
                      {column.Field}
                      {column.Key === 'PRI' && (
                        <span className="ml-1 text-yellow-500">🔑</span>
                      )}
                    </div>
                    <div className="text-xs text-gray-400 normal-case">
                      {column.Type}
                    </div>
                  </th>
                ))}
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {memoizedData.map((row, index) => (
                <motion.tr
                  key={index}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.1 }} // Minimal animation for better performance
                  className="hover:bg-gray-50"
                >
                  {memoizedStructure.map((column) => (
                    <td
                      key={column.Field}
                      className="px-6 py-4 text-sm text-gray-900 border-b border-gray-100"
                    >
                      <div className="max-w-xs truncate">
                        {row[column.Field] !== null ? String(row[column.Field]) : (
                          <span className="text-gray-400 italic">NULL</span>
                        )}
                      </div>
                    </td>
                  ))}
                  <td className="px-6 py-4 text-right text-sm font-medium border-b border-gray-100">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => handleEdit(row)}
                        className="text-blue-600 hover:text-blue-800 p-1 rounded transition-colors"
                      >
                        <SafeIcon icon={FiEdit2} />
                      </button>
                      <button
                        onClick={() => handleDelete(row)}
                        className="text-red-600 hover:text-red-800 p-1 rounded transition-colors"
                      >
                        <SafeIcon icon={FiTrash2} />
                      </button>
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="text-center py-12">
            <div className="text-gray-400 text-4xl mb-4">📊</div>
            <h3 className="text-lg font-medium text-gray-800 mb-2">No Data Found</h3>
            <p className="text-gray-600">This table appears to be empty or no rows match your filters.</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} results
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page <= 1}
              className="p-2 text-gray-600 hover:text-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <SafeIcon icon={FiChevronLeft} />
            </button>
            <span className="px-3 py-1 bg-gray-100 rounded text-sm">
              {pagination.page} / {pagination.totalPages}
            </span>
            <button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page >= pagination.totalPages}
              className="p-2 text-gray-600 hover:text-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <SafeIcon icon={FiChevronRight} />
            </button>
          </div>
        </div>
      )}

      {/* Modals */}
      <AnimatePresence>
        {editingRow && (
          <EditModal
            row={editingRow}
            structure={memoizedStructure}
            onSave={handleSave}
            onClose={() => setEditingRow(null)}
          />
        )}
        {showAddModal && (
          <AddRowModal
            structure={memoizedStructure}
            onSave={handleAdd}
            onClose={() => setShowAddModal(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default DataTable;